import React, { useEffect, useState } from 'react';
import {
  Inbox, ShieldCheck, RefreshCw, CalendarClock,
} from 'lucide-react';
import { payoutAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

/**
 * AccountingSnapshot — third Command Center band on the Accounting
 * dashboard. Four executive-signal cells. Each cell's hint text names its
 * concrete backend source (per Phase 20.7 "Each metric must explain its
 * source" spec).
 *
 *   1. Pending Items      → sum of EFT records with status='Pending Review'
 *                           + statements in status='Draft'.
 *   2. Completed Reviews  → EFT records with status='Approved' (records the
 *                           accountant has cleared).
 *   3. Recent Updates     → count of ledger import batches (each batch = a
 *                           recent data-ingest event).
 *   4. Last Activity      → most recent EFT `updated_at` timestamp across
 *                           records this accountant can see, formatted as
 *                           a friendly date. Falls back to '—' if none.
 *
 * No $ amounts. No revenue or ranking signals.
 */

const Cell = ({ label, value, hint, icon: Icon, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    {Icon && (
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600">
        <Icon size={17} />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="text-sm font-semibold mt-0.5 text-slate-900 truncate">
        {value ?? '—'}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  </div>
);

const readList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.statements)) return payload.statements;
  return [];
};

const mostRecent = (list, field = 'updated_at') => {
  const times = list
    .map((r) => new Date(r?.[field] || r?.created_at || 0).getTime())
    .filter((t) => t > 0);
  if (times.length === 0) return null;
  return new Date(Math.max(...times)).toISOString();
};

export const AccountingSnapshot = () => {
  const [state, setState] = useState({
    pending: 0, approved: 0, batches: 0, lastActivity: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [eftAll, stmtDraft, ledgerStats] = await Promise.all([
        payoutAPI.eft.list().catch(() => null),
        payoutAPI.statements.list({ status: 'Draft' }).catch(() => null),
        payoutAPI.ledger.stats().catch(() => null),
      ]);
      if (cancelled) return;
      const efts = readList(eftAll?.data);
      const eftPending = efts.filter((r) => r?.status === 'Pending Review').length;
      const eftApproved = efts.filter((r) => r?.status === 'Approved').length;
      const draftStmts = readList(stmtDraft?.data).length;
      setState({
        pending: eftPending + draftStmts,
        approved: eftApproved,
        batches: ledgerStats?.data?.import_batch_count || 0,
        lastActivity: mostRecent(efts),
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="mb-8" data-testid="accounting-snapshot">
      <div className="mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Accounting Snapshot
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          A quick read on your review inbox and how much data has flowed
          through recently.
        </p>
      </div>

      <Card className="animate-slideIn">
        <CardContent className="p-5">
          {loading ? (
            <div className="py-6 text-center text-sm text-slate-400" data-testid="accounting-snapshot-loading">
              Loading snapshot…
            </div>
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
              <Cell
                label="Pending Items"
                value={state.pending}
                icon={Inbox}
                hint="EFT records in Pending Review + draft statements"
                testId="accounting-snapshot-pending-items"
              />
              <Cell
                label="Completed Reviews"
                value={state.approved}
                icon={ShieldCheck}
                hint="EFT records you have already approved"
                testId="accounting-snapshot-completed-reviews"
              />
              <Cell
                label="Recent Updates"
                value={state.batches}
                icon={RefreshCw}
                hint="Distinct ledger import batches on file"
                testId="accounting-snapshot-recent-updates"
              />
              <Cell
                label="Last Activity"
                value={state.lastActivity ? formatDate(state.lastActivity) : '—'}
                icon={CalendarClock}
                hint="Most recent EFT record update visible to you"
                testId="accounting-snapshot-last-activity"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default AccountingSnapshot;
